import { useEffect, useState } from 'react'

import { Badge } from './Badge'

import type { PrefillOrigin } from '@/api/internal'
import Button from '@/components/Button'
import { Callout } from '@/components/Callout/Callout'
import { Spinner } from '@/components/Spinner'
import { PREFILL_ORIGIN_LABEL } from '@/pages/app/AttributionViewer/annotate/gtModel'
import type { SeedState } from '@/pages/app/AttributionViewer/annotate/seedState'

export const SEED_SLOW_AFTER_MS = 10 * 60 * 1000

interface SeedStatusProps {
  state: SeedState
  prefillOrigin: PrefillOrigin
  checking: boolean
  onCheckAgain: () => void
}

export const SeedStatus = ({ state, prefillOrigin, checking, onCheckAgain }: SeedStatusProps) => {
  const [slow, setSlow] = useState(false)

  useEffect(() => {
    if (state === 'blocked') return
    const timer = window.setTimeout(() => setSlow(true), SEED_SLOW_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [state])

  return (
    <Callout.Root>
      <Callout.Header>
        <Callout.Title>{state === 'blocked' ? "Can't annotate this meeting" : 'Preparing this meeting'}</Callout.Title>
        {state !== 'deciding' && <Badge>Seed: {PREFILL_ORIGIN_LABEL[prefillOrigin]}</Badge>}
      </Callout.Header>
      {state !== 'blocked' ? (
        <div role="status" aria-live="polite">
          <Spinner size={16} color="currentColor" />
          <Callout.Description>
            {state === 'deciding'
              ? "This meeting's draft source is still being chosen."
              : 'Word timing for the Chorus draft is still being computed.'}{' '}
            This usually takes a few minutes. You can leave and come back.
          </Callout.Description>
          {slow && (
            <>
              <Callout.Description>This is taking longer than usual.</Callout.Description>
              <Button title="Check again" type="light" isDisabled={checking} onClick={onCheckAgain} />
            </>
          )}
        </div>
      ) : (
        <Callout.Description>
          This meeting&apos;s audio is no longer available, so it can&apos;t be annotated.
        </Callout.Description>
      )}
    </Callout.Root>
  )
}
